// Inspector 의 애니 값(시작·중간·끝) 편집 순수 로직.
// 저장은 ItemAnimation.customOutputRanges — 렌더 엔진(calculate-animation)이 읽는 그 필드다.
// inputRange 와 길이가 다르면 엔진이 기본값으로 폴백하므로, 편집은 항상 길이를 보존한다.
import {
  ANIMATION_META,
  type AnimationMeta,
  type AnimationProperty,
  type AnimationPropertyAnimation,
  type ItemAnimation,
} from './animation-item-type';

// property 별 입력 UI 스펙. translate 단위는 아이템 자기 크기 기준 %(캔버스 px 아님).
export const ANIMATION_PROPERTY_INPUT: Record<
  AnimationProperty,
  { min: number; max: number; step: number; unit?: string }
> = {
  opacity: { min: 0, max: 1, step: 0.05 },
  scale: { min: 0, max: 5, step: 0.05 },
  rotation: { min: -360, max: 360, step: 1, unit: '°' },
  // kenBurns 기본 드리프트가 -2 / -1.5% 라 정수 step 이면 미세조정이 안 된다.
  translateX: { min: -500, max: 500, step: 0.5, unit: '%' },
  translateY: { min: -500, max: 500, step: 0.5, unit: '%' },
  // 캔버스 px 이동(move) — 세로 캔버스 1920px 기준 왕복까지 여유 있게.
  translateXPx: { min: -4000, max: 4000, step: 1, unit: 'px' },
  translateYPx: { min: -4000, max: 4000, step: 1, unit: 'px' },
  clipLeft: { min: 0, max: 100, step: 1, unit: '%' },
  clipRight: { min: 0, max: 100, step: 1, unit: '%' },
  clipTop: { min: 0, max: 100, step: 1, unit: '%' },
  clipBottom: { min: 0, max: 100, step: 1, unit: '%' },
  blur: { min: 0, max: 50, step: 1, unit: 'px' },
};

/** keyframe 개수별 라벨 키(i18n: Editor.inspector.animation.{key}). 2개면 시작·끝, 3개면 중간까지. */
export const getKeyframeLabelKeys = (length: number): string[] => {
  if (length === 2) return ['start', 'end'];
  if (length === 3) return ['start', 'middle', 'end'];
  // 4개 이상은 이름 붙이기 애매하므로 첫·끝만 이름을 주고 나머지는 번호로 표시한다.
  return Array.from({ length }, (_, index) => {
    if (index === 0) return 'start';
    if (index === length - 1) return 'end';
    return `${index + 1}`;
  });
};

/** 그 애니가 쓰는 property 목록(메타 정의 순서). */
export const getEditableProperties = (
  animation: ItemAnimation,
): AnimationPropertyAnimation[] =>
  (ANIMATION_META[animation.type] as AnimationMeta).properties;

/**
 * 편집 UI 에 보여줄 현재 outputRange.
 * custom 값이 있고 inputRange 와 길이가 맞으면 그것을, 아니면 메타 기본값을 쓴다
 * (엔진 calculate-animation 의 폴백 규칙과 동일 — 화면과 렌더가 갈리지 않게).
 */
export const resolveEditableOutputRange = (
  animation: ItemAnimation,
  propAnim: AnimationPropertyAnimation,
): number[] => {
  const inputRange =
    animation.customInputRanges?.[propAnim.property] ?? propAnim.inputRange;
  const custom = animation.customOutputRanges?.[propAnim.property];
  if (custom && custom.length === inputRange.length) return custom;
  return propAnim.outputRange.length === inputRange.length
    ? propAnim.outputRange
    : [];
};

/**
 * keyframe 하나의 값을 바꾼 새 ItemAnimation 을 만든다(길이 보존).
 * 범위를 벗어난 index 나 유한하지 않은 값은 무시하고 원본을 그대로 돌려준다.
 *
 * expectedLength: 다중 선택 일괄 적용용 가드. index 는 "몇 번째 keyframe" 이라 keyframe
 * 개수가 다른 아이템에 그대로 쓰면 같은 index 가 다른 의미(중간 vs 끝)를 가리켜 조용히
 * 엉뚱한 값을 덮어쓴다. 편집 UI 가 본 길이와 다르면 그 아이템은 건너뛴다.
 */
export const setOutputRangeValue = (
  animation: ItemAnimation,
  property: AnimationProperty,
  index: number,
  value: number,
  expectedLength?: number,
): ItemAnimation => {
  if (!Number.isFinite(value)) return animation;

  const propAnim = getEditableProperties(animation).find(
    (p) => p.property === property,
  );
  if (!propAnim) return animation;

  const current = resolveEditableOutputRange(animation, propAnim);
  if (expectedLength !== undefined && current.length !== expectedLength) {
    return animation;
  }
  if (index < 0 || index >= current.length) return animation;
  if (current[index] === value) return animation;

  const next = [...current];
  next[index] = value;

  return {
    ...animation,
    customOutputRanges: {
      ...animation.customOutputRanges,
      [property]: next,
    },
  };
};
